import type { STATUS_REWARD_BENEFITS } from '../constants/statusRewardBenefits';
import type { LockBadgeKind } from '../types/gamificationBadges';
import type { GmRewardPerk, GmStatusKey, StatusRewardBenefit, StatusRewardView } from '../types/statusRewards';
import type { GroupedPlayerRewardDto } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import type { ExtendedProgressStatusDto } from '@/queries/progress-statuses/progress-statuses.schema';
import { GamificationRewardType } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import { statusKeys } from '../constants/statusKeys';
import { getBenefitLockBadges } from './getBenefitLockBadges';
import { getStatusKey } from './getStatusKey';
import { getStatusRewardBenefits } from './getStatusRewardBenefits';

export const FIRST_PLAYER_LEVEL = 1;

export type BenefitAvailability = 'available' | 'needs-status' | 'coming-soon' | 'unavailable';

export interface ResolvedStatusRewardBenefit {
  benefit: (typeof STATUS_REWARD_BENEFITS)[number];
  availability: BenefitAvailability;
  statusName?: string;
  groupedReward?: GroupedPlayerRewardDto;
  badges: ReturnType<typeof getBenefitLockBadges>;
}

type Benefit = StatusRewardBenefit;
type AvailabilityResult = Pick<ResolvedStatusRewardBenefit, 'availability' | 'statusName'>;

interface Params {
  currentStatusKey?: GmStatusKey;
  groupedRewards?: GroupedPlayerRewardDto[];
  progressStatuses?: ExtendedProgressStatusDto[];
  playerLevel: number;
  view?: StatusRewardView;
}

const resolveStatusRequirement = (
  benefit: Benefit,
  groupedReward: GroupedPlayerRewardDto | undefined,
  { currentStatusKey, progressStatuses, playerLevel }: Params,
): AvailabilityResult => {
  // Referral is always available and must not be locked by backend level data.
  if (benefit.type === GamificationRewardType.Referral) {
    return { availability: 'available' };
  }

  const isFirstLevelCard = benefit.type === GamificationRewardType.LevelUp && playerLevel === FIRST_PLAYER_LEVEL;
  const requiredLevel = groupedReward?.minLevelFrom;

  if (typeof requiredLevel === 'number') {
    return {
      availability: isFirstLevelCard || playerLevel >= requiredLevel ? 'available' : 'needs-status',
      statusName: progressStatuses?.find(status => status.levelFrom === requiredLevel)?.name,
    };
  }

  // Scratch cards have no status fallback when the API provides no level.
  if (benefit.type === GamificationRewardType.LevelUp || benefit.type === GamificationRewardType.StatusUp) {
    return { availability: isFirstLevelCard ? 'available' : 'unavailable' };
  }

  if (!benefit.unlocksWithStatus && !benefit.comingSoon) {
    return { availability: 'unavailable' };
  }

  const requiredStatusKey = statusKeys.find(key => benefit.values[key]);
  if (!requiredStatusKey) {
    return { availability: 'unavailable' };
  }

  // A player without a progress status is on the first tier.
  const isAvailable = statusKeys.indexOf(currentStatusKey ?? statusKeys[0]) >= statusKeys.indexOf(requiredStatusKey);

  return {
    availability: isAvailable ? 'available' : 'needs-status',
    statusName: isAvailable
      ? undefined
      : progressStatuses?.find(status => getStatusKey(status) === requiredStatusKey)?.name,
  };
};

const resolveAvailability = (
  benefit: Benefit,
  groupedReward: GroupedPlayerRewardDto | undefined,
  params: Params,
): AvailabilityResult => {
  if (!benefit.comingSoon) {
    return resolveStatusRequirement(benefit, groupedReward, params);
  }

  // The statuses page lists every perk, so the frontend launch flag takes priority there.
  if (params.view !== 'rewards') {
    return { availability: 'coming-soon' };
  }

  // The rewards grid shows Coming soon only once the player has the required status.
  const requirement = resolveStatusRequirement(benefit, groupedReward, params);
  return requirement.availability === 'needs-status' ? requirement : { availability: 'coming-soon' };
};

/** Builds the shared benefit state for the statuses view and rewards grid. */
export const buildStatusRewardBenefits = (params: Params): ResolvedStatusRewardBenefit[] => {
  return getStatusRewardBenefits(params.view ?? 'statuses').map((benefit) => {
    const groupedReward = params.groupedRewards?.find(reward => reward.type === benefit.type);
    const { availability, statusName } = resolveAvailability(benefit, groupedReward, params);

    let badgeKind: LockBadgeKind | null = null;
    if (availability === 'coming-soon') {
      badgeKind = 'coming-soon';
    } else if (availability === 'needs-status') {
      badgeKind = 'needs-status';
    }

    return {
      benefit,
      groupedReward,
      availability,
      statusName,
      badges: getBenefitLockBadges({ type: benefit.type, kind: badgeKind, statusName }),
    };
  });
};

/**
 * Used for building mobile status reward perks
 */
export const buildStatusRewardPerks = (benefits: ResolvedStatusRewardBenefit[], statusKey?: GmStatusKey) => {
  if (!statusKey) {
    return [];
  }

  return benefits.map(({ benefit, availability, badges }) => {
    const subTitle = benefit.values[statusKey] ?? '';
    const perkItem: GmRewardPerk = {
      id: benefit.translationKey,
      action: availability === 'available' ? benefit.action : undefined,
      img: benefit.perkImg,
      type: benefit.type,
      title: benefit.title,
      subTitle,
      isDisabled: !subTitle,
    };

    return { perkItem, availability, lockBadge: badges.statuses, action: benefit.action };
  });
};
